import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const { resolved } = useTheme();

    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close menu on route change
    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    // Lock body scroll when menu is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'What we Build', path: '/products' },
        { name: 'Careers', path: '/careers' },
        { name: 'About Us', path: '/about-us' },
    ];

    return (
        <>
            {/* Fixed Brand Logo (Top Left - Hidden on Mobile) */}
            <Link
                to="/"
                className="fixed top-4 left-4 md:top-6 md:left-6 z-50 flex items-center gap-4 group transition-opacity hover:opacity-80 hidden md:flex"
            >
                <img
                    src={resolved === 'light' ? '/antz-logo-v2.png' : '/antz-logo.png'}
                    alt="Antz"
                    className="h-[60px] w-[280px] object-contain"
                />
            </Link>

            {/* Floating Navigation Dock (Centered) */}
            <nav className={`fixed top-6 left-1/2 -translate-x-1/2 z-40 transition-all duration-500 ${scrolled ? 'w-[90%] md:w-[620px]' : 'w-[95%] md:w-[700px]'}`}>
                <div className={`relative bg-background/60 backdrop-blur-xl border border-overlay/[0.08] rounded-full px-2 py-1 shadow-lg transition-all duration-500`}>
                    <div className="flex items-center justify-between pl-2 pr-2">
                        {/* Mobile Logo (Inside pill) */}
                        <Link to="/" className="md:hidden flex-shrink-0 ml-2">
                            <img
                                src={resolved === 'light' ? '/antz-logo-v2.png' : '/antz-logo.png'}
                                alt="Antz"
                                className="h-[32px] w-[auto] object-contain"
                            />
                        </Link>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center gap-2 mx-auto">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    className={`relative px-5 py-2.5 rounded-full text-[14px] font-medium tracking-tight whitespace-nowrap transition-all duration-300 hover:text-foreground ${location.pathname === link.path ? 'text-primary' : 'text-muted-foreground hover:bg-white/[0.03]'}`}
                                >
                                    {link.name}
                                    {location.pathname === link.path && (
                                        <motion.span
                                            layoutId="nav-pill"
                                            className="absolute inset-0 bg-white/[0.05] rounded-full -z-10"
                                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                        />
                                    )}
                                </Link>
                            ))}
                        </div>

                        {/* CTA */}
                        <div className="flex items-center gap-4 ml-auto md:ml-0">
                            <Link to="/contact-us" className="hidden md:block px-5 py-2 rounded-full bg-primary text-primary-foreground text-[13px] font-medium hover:opacity-90 transition-opacity">
                                Contact
                            </Link>

                            {/* Mobile Menu Button */}
                            <button
                                className="md:hidden p-2 text-foreground"
                                onClick={() => setIsOpen(!isOpen)}
                                aria-label={isOpen ? 'Close menu' : 'Open menu'}
                            >
                                {isOpen ? (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                ) : (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-30 bg-background/95 backdrop-blur-xl md:hidden"
                    >
                        <div className="flex flex-col items-center justify-center h-full gap-6">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    className={`text-2xl font-medium transition-colors ${location.pathname === link.path ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                                >
                                    {link.name}
                                </Link>
                            ))}
                            <Link
                                to="/contact-us"
                                className="mt-4 px-8 py-3 rounded-full bg-primary text-primary-foreground text-base font-medium hover:opacity-90 transition-opacity"
                            >
                                Contact Us
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
